import * as v from "valibot";

export const SessionRoleSchema = v.picklist(["guest", "user", "admin"]);

export const UserSchema = v.object({
  id: v.string(),
  email: v.nullable(v.string()),
  name: v.nullable(v.string()),
  role: SessionRoleSchema,
});

export const SessionSchema = v.object({
  authenticated: v.boolean(),
  user: v.nullable(UserSchema),
});

export const SupportedLanguageCodeSchema = v.picklist([
  "en",
  "ja",
  "zh-Hans",
  "ko",
  "es",
  "fr",
  "de",
  "pt",
]);
export const LanguagePreferenceSchema = v.picklist([
  "auto",
  "en",
  "ja",
  "zh-Hans",
  "ko",
  "es",
  "fr",
  "de",
  "pt",
]);

// Shared language metadata keeps workbench and guest surfaces aligned when users explicitly steer the assist direction.
export const LANGUAGE_OPTIONS = [
  { value: "auto", label: "Auto" },
  { value: "en", label: "English" },
  { value: "ja", label: "Japanese" },
  { value: "zh-Hans", label: "Chinese (Simplified)" },
  { value: "ko", label: "Korean" },
  { value: "es", label: "Spanish" },
  { value: "fr", label: "French" },
  { value: "de", label: "German" },
  { value: "pt", label: "Portuguese" },
] as const satisfies ReadonlyArray<{
  value: v.InferOutput<typeof LanguagePreferenceSchema>;
  label: string;
}>;

export const ConvertFeatureSchema = v.picklist(["interlingua", "phrasing", "phonology"]);

// Convert feature metadata keeps workbench and guest surfaces aligned on the three inference-bridge modes.
// Order follows the canonical product sequence: Interlanguage Resolution -> Phrasing Suggestions -> Phonetic Inference.
export const CONVERT_FEATURE_OPTIONS = [
  {
    value: "interlingua",
    label: "Interlanguage Resolution",
    description: "Resolve unfinished bilingual input into a natural target-language draft.",
  },
  {
    value: "phrasing",
    label: "Phrasing Suggestions",
    description: "Keep your intent but improve wording, tone, and relationship fit.",
  },
  {
    value: "phonology",
    label: "Phonetic Inference",
    description: "Recover a likely word or phrase from a sound-based guess.",
  },
] as const satisfies ReadonlyArray<{
  value: v.InferOutput<typeof ConvertFeatureSchema>;
  label: string;
  description: string;
}>;

// Convert requests stay input-centric so the API can preserve unfinished language while returning a full inference snapshot.
export const ConvertRequestSchema = v.object({
  feature: v.optional(ConvertFeatureSchema),
  sourceInput: v.pipe(v.string(), v.minLength(1), v.maxLength(5000)),
  targetLanguage: v.optional(SupportedLanguageCodeSchema),
  nativeLanguage: v.optional(SupportedLanguageCodeSchema),
  followInputLanguage: v.optional(v.boolean(), false),
  includeExplanation: v.optional(v.boolean(), false),
  options: v.optional(
    v.object({
      saveHistory: v.optional(v.boolean(), false),
    }),
    {},
  ),
});

export const CandidateOptionSchema = v.object({
  id: v.string(),
  text: v.pipe(v.string(), v.minLength(1)),
  score: v.optional(v.pipe(v.number(), v.minValue(0), v.maxValue(1))),
  recommended: v.optional(v.boolean()),
  reason: v.optional(v.string()),
});

export const CandidateSpanStatusSchema = v.picklist(["active", "applied", "invalidated"]);

// Highlighted alternatives stay range-aware so the workbench can keep manual approval and in-place review.
export const CandidateSpanSchema = v.object({
  id: v.string(),
  start: v.pipe(v.number(), v.integer(), v.minValue(0)),
  end: v.pipe(v.number(), v.integer(), v.minValue(1)),
  sourceText: v.pipe(v.string(), v.minLength(1)),
  candidates: v.pipe(v.array(CandidateOptionSchema), v.minLength(1), v.maxLength(8)),
  scope: v.picklist(["token", "phrase"]),
  explanation: v.optional(v.string()),
  status: v.optional(CandidateSpanStatusSchema),
  appliedCandidateId: v.optional(v.string()),
});

export const DiagnosticTraceStepSchema = v.object({
  label: v.string(),
  detail: v.string(),
});

// The convert API returns one full inference snapshot per run so history can replay exactly what the learner saw.
export const ConvertResultSchema = v.object({
  feature: ConvertFeatureSchema,
  sourceInput: v.string(),
  interpretedIntent: v.optional(v.string()),
  targetOutput: v.string(),
  alternatives: v.array(CandidateSpanSchema),
  diagnosticTrace: v.optional(v.array(DiagnosticTraceStepSchema)),
  confidence: v.optional(v.number()),
  explanation: v.optional(v.string()),
  resolvedTargetLanguage: SupportedLanguageCodeSchema,
  resolvedNativeLanguage: SupportedLanguageCodeSchema,
  createdAt: v.string(),
});

export const HistoryEntrySchema = v.object({
  id: v.string(),
  feature: ConvertFeatureSchema,
  sourceInput: v.string(),
  interpretedIntent: v.optional(v.string()),
  targetOutput: v.string(),
  targetLanguage: v.optional(LanguagePreferenceSchema, "auto"),
  nativeLanguage: v.optional(LanguagePreferenceSchema, "ja"),
  // Persisting the follow-input toggle keeps restored runs aligned with the user's assist steering, not just the raw values.
  followInputLanguage: v.optional(v.boolean(), false),
  alternatives: v.array(CandidateSpanSchema),
  diagnosticTrace: v.optional(v.array(DiagnosticTraceStepSchema)),
  confidence: v.optional(v.number()),
  explanation: v.optional(v.string()),
  resolvedTargetLanguage: v.optional(SupportedLanguageCodeSchema),
  resolvedNativeLanguage: v.optional(SupportedLanguageCodeSchema),
  createdAt: v.string(),
});

export const PublicManifestSchema = v.object({
  slug: v.string(),
  feature: ConvertFeatureSchema,
  title: v.string(),
  description: v.string(),
  inputHint: v.string(),
  related: v.array(v.string()),
});

export const SearchResultSchema = v.object({
  slug: v.string(),
  title: v.string(),
  description: v.string(),
});

export function parseSession(value: unknown): Session | null {
  const result = v.safeParse(SessionSchema, value);
  return result.success ? result.output : null;
}

export function parsePublicManifest(value: unknown): PublicManifest | null {
  const result = v.safeParse(PublicManifestSchema, value);
  return result.success ? result.output : null;
}

export function parseSearchResults(value: unknown): SearchResult[] | null {
  const result = v.safeParse(v.array(SearchResultSchema), value);
  return result.success ? result.output : null;
}

export const DraftSchema = v.object({
  id: v.string(),
  feature: v.optional(ConvertFeatureSchema, "interlingua"),
  sourceInput: v.string(),
  interpretedIntent: v.optional(v.string()),
  targetOutput: v.optional(v.string()),
  targetLanguage: v.optional(LanguagePreferenceSchema, "auto"),
  nativeLanguage: v.optional(LanguagePreferenceSchema, "ja"),
  // Drafts must remember whether native language was fixed or source-following so the next assist run behaves predictably.
  followInputLanguage: v.optional(v.boolean(), false),
  alternatives: v.array(CandidateSpanSchema),
  diagnosticTrace: v.optional(v.array(DiagnosticTraceStepSchema)),
  explanation: v.optional(v.string()),
  resolvedTargetLanguage: v.optional(SupportedLanguageCodeSchema),
  resolvedNativeLanguage: v.optional(SupportedLanguageCodeSchema),
  updatedAt: v.string(),
});

export const TrialHandoffSnapshotSchema = v.object({
  schemaVersion: v.literal(1),
  source: v.picklist(["public-trial"]),
  feature: ConvertFeatureSchema,
  sourceInput: v.string(),
  interpretedIntent: v.optional(v.string()),
  targetOutput: v.string(),
  targetLanguage: v.optional(LanguagePreferenceSchema, "auto"),
  nativeLanguage: v.optional(LanguagePreferenceSchema, "ja"),
  followInputLanguage: v.optional(v.boolean(), false),
  alternatives: v.array(CandidateSpanSchema),
  diagnosticTrace: v.optional(v.array(DiagnosticTraceStepSchema)),
  confidence: v.optional(v.number()),
  explanation: v.optional(v.string()),
  resolvedTargetLanguage: SupportedLanguageCodeSchema,
  resolvedNativeLanguage: SupportedLanguageCodeSchema,
  createdAt: v.string(),
});

export function parseTrialHandoffSnapshot(value: unknown): TrialHandoffSnapshot | null {
  const result = v.safeParse(TrialHandoffSnapshotSchema, value);
  return result.success ? result.output : null;
}

export const SignInRequestSchema = v.object({
  email: v.pipe(v.string(), v.email()),
  password: v.pipe(v.string(), v.minLength(8)),
  redirectTo: v.optional(v.string(), "/"),
});

export const MobileExchangeRequestSchema = v.object({
  email: v.pipe(v.string(), v.email()),
  platform: v.picklist(["ios", "android"]),
});

export const MobileSessionSchema = v.object({
  token: v.string(),
  email: v.pipe(v.string(), v.email()),
  platform: v.picklist(["ios", "android"]),
  expiresAt: v.string(),
  user: UserSchema,
});

export function parseMobileSession(value: unknown): MobileSession | null {
  const result = v.safeParse(MobileSessionSchema, value);
  return result.success ? result.output : null;
}

export const OrganizationSchema = v.object({
  id: v.string(),
  name: v.string(),
  organizationType: v.picklist(["general", "education", "enterprise"]),
  status: v.picklist(["active", "suspended"]),
  createdAt: v.string(),
});

export const AdminDashboardSchema = v.object({
  users: v.number(),
  organizations: v.number(),
  conversions: v.number(),
});

export const BillingPlanCodeSchema = v.picklist(["free", "pro", "team"]);
export const BillingChannelSchema = v.picklist([
  "stripe_managed_payments",
  "app_store",
  "google_play",
  "admin_manual",
]);
export const BillingIntervalSchema = v.picklist(["month", "year"]);
export const EntitlementScopeSchema = v.picklist(["user", "organization"]);
export const BillingStatusSchema = v.picklist([
  "active",
  "trialing",
  "grace_period",
  "canceled",
  "expired",
  "revoked",
]);

export const CatalogPriceSchema = v.object({
  interval: BillingIntervalSchema,
  amount: v.number(),
  currency: v.string(),
  priceId: v.nullable(v.string()),
});

export const CatalogPlanSchema = v.object({
  code: BillingPlanCodeSchema,
  name: v.string(),
  description: v.string(),
  features: v.array(v.string()),
  prices: v.array(CatalogPriceSchema),
  channels: v.array(BillingChannelSchema),
  recommended: v.optional(v.boolean()),
});

export function parseCatalogPlans(value: unknown): CatalogPlan[] | null {
  const result = v.safeParse(v.array(CatalogPlanSchema), value);
  return result.success ? result.output : null;
}

export const BillingSubjectSchema = v.object({
  id: v.string(),
  scope: EntitlementScopeSchema,
  userId: v.optional(v.string()),
  organizationId: v.optional(v.string()),
  createdAt: v.string(),
});

export const EntitlementSchema = v.object({
  id: v.string(),
  subjectId: v.string(),
  scope: EntitlementScopeSchema,
  scopeReferenceId: v.string(),
  planCode: BillingPlanCodeSchema,
  billingChannel: BillingChannelSchema,
  status: BillingStatusSchema,
  startsAt: v.string(),
  endsAt: v.optional(v.nullable(v.string())),
  sourceType: v.picklist(["subscription", "manual_grant", "native_purchase"]),
  sourceId: v.string(),
});

export const BillingSubscriptionSchema = v.object({
  id: v.string(),
  subjectId: v.string(),
  planCode: BillingPlanCodeSchema,
  billingChannel: BillingChannelSchema,
  status: BillingStatusSchema,
  interval: BillingIntervalSchema,
  quantity: v.number(),
  externalSubscriptionId: v.optional(v.nullable(v.string())),
  externalPriceId: v.optional(v.nullable(v.string())),
  cancelAtPeriodEnd: v.boolean(),
  currentPeriodEnd: v.optional(v.nullable(v.string())),
});

export function isProductBillingReturnPath(value: string) {
  if (!value.startsWith("/") || value.startsWith("//")) {
    return false;
  }

  try {
    const url = new URL(value, "https://product.fluentry.test");
    if (url.origin !== "https://product.fluentry.test" || url.pathname !== "/settings/billing") {
      return false;
    }

    let hasUnsupportedParam = false;
    url.searchParams.forEach((_value, key) => {
      if (key !== "checkout" && key !== "plan" && key !== "source") {
        hasUnsupportedParam = true;
      }
    });
    if (hasUnsupportedParam) {
      return false;
    }

    const checkout = url.searchParams.get("checkout");
    const plan = url.searchParams.get("plan");
    const source = url.searchParams.get("source");
    return (
      (checkout === null || checkout === "success" || checkout === "cancel") &&
      (plan === null || plan === "pro" || plan === "team") &&
      (source === null || source === "quota")
    );
  } catch {
    return false;
  }
}

const ProductBillingReturnPathSchema = v.pipe(
  v.string(),
  v.check(
    isProductBillingReturnPath,
    "Billing return paths must stay on /settings/billing and use supported query parameters.",
  ),
);

export function isBillingCheckoutScopeValid(value: {
  planCode: "pro" | "team";
  interval: "month" | "year";
  organizationId?: string;
  quantity: number;
  successPath: string;
  cancelPath: string;
  upgradeReason?: "saved_run_quota";
}) {
  if (value.planCode === "team") {
    return typeof value.organizationId === "string" && value.organizationId.length > 0;
  }

  return value.organizationId === undefined;
}

export const BillingCheckoutRequestSchema = v.pipe(
  v.object({
    planCode: v.picklist(["pro", "team"]),
    interval: BillingIntervalSchema,
    organizationId: v.optional(v.string()),
    quantity: v.optional(v.pipe(v.number(), v.integer(), v.minValue(1)), 1),
    successPath: v.optional(ProductBillingReturnPathSchema, "/settings/billing?checkout=success"),
    cancelPath: v.optional(ProductBillingReturnPathSchema, "/settings/billing?checkout=cancel"),
    upgradeReason: v.optional(v.picklist(["saved_run_quota"])),
  }),
  v.check(
    (value) => isBillingCheckoutScopeValid(value),
    "Pro checkout must be user-scoped and Team checkout must include an organization.",
  ),
);

export const BillingPortalActionSchema = v.object({
  action: v.picklist(["change_plan", "cancel", "resume"]),
  planCode: v.optional(v.picklist(["pro", "team"])),
  interval: v.optional(BillingIntervalSchema),
  organizationId: v.optional(v.string()),
});

export const NativePurchaseValidationSchema = v.object({
  planCode: v.literal("pro"),
  platform: v.picklist(["ios", "android"]),
  transactionId: v.string(),
  purchaseToken: v.optional(v.string()),
  productId: v.string(),
  environment: v.optional(v.picklist(["sandbox", "production"]), "sandbox"),
});

export const AdminEntitlementGrantSchema = v.object({
  organizationId: v.pipe(v.string(), v.minLength(1)),
  planCode: v.picklist(["pro", "team"]),
  startsAt: v.optional(v.string()),
  endsAt: v.optional(v.nullable(v.string())),
  note: v.pipe(v.string(), v.minLength(1), v.maxLength(500)),
});

export const BillingUsageQuotaSchema = v.object({
  periodStart: v.string(),
  periodEnd: v.string(),
  savedRuns: v.object({
    used: v.number(),
    limit: v.nullable(v.number()),
    remaining: v.nullable(v.number()),
    limitReached: v.boolean(),
  }),
});

export const EffectiveEntitlementSchema = v.object({
  viewerUserId: v.string(),
  effectivePlanCode: BillingPlanCodeSchema,
  effectiveStatus: BillingStatusSchema,
  sourceScopes: v.array(EntitlementScopeSchema),
  directEntitlements: v.array(EntitlementSchema),
  organizationEntitlements: v.array(EntitlementSchema),
  subscriptions: v.array(BillingSubscriptionSchema),
  usage: BillingUsageQuotaSchema,
});

export const ApiSuccessSchema = <T extends v.BaseSchema<unknown, unknown, v.BaseIssue<unknown>>>(
  dataSchema: T,
) =>
  v.object({
    success: v.literal(true),
    result: dataSchema,
  });

export const ApiErrorSchema = v.object({
  success: v.literal(false),
  error: v.object({
    code: v.string(),
    message: v.string(),
  }),
});

export type Session = v.InferOutput<typeof SessionSchema>;
export type SupportedLanguageCode = v.InferOutput<typeof SupportedLanguageCodeSchema>;
export type LanguagePreference = v.InferOutput<typeof LanguagePreferenceSchema>;
export type ConvertFeature = v.InferOutput<typeof ConvertFeatureSchema>;
export type ConvertRequestInput = v.InferInput<typeof ConvertRequestSchema>;
export type ConvertRequest = v.InferOutput<typeof ConvertRequestSchema>;
export type CandidateOption = v.InferOutput<typeof CandidateOptionSchema>;
export type CandidateSpan = v.InferOutput<typeof CandidateSpanSchema>;
export type DiagnosticTraceStep = v.InferOutput<typeof DiagnosticTraceStepSchema>;
export type ConvertResult = v.InferOutput<typeof ConvertResultSchema>;
export type HistoryEntry = v.InferOutput<typeof HistoryEntrySchema>;
export type PublicManifest = v.InferOutput<typeof PublicManifestSchema>;
export type SearchResult = v.InferOutput<typeof SearchResultSchema>;
export type Draft = v.InferOutput<typeof DraftSchema>;
export type TrialHandoffSnapshot = v.InferOutput<typeof TrialHandoffSnapshotSchema>;
export type SignInRequest = v.InferOutput<typeof SignInRequestSchema>;
export type MobileExchangeRequest = v.InferOutput<typeof MobileExchangeRequestSchema>;
export type MobileSession = v.InferOutput<typeof MobileSessionSchema>;
export type Organization = v.InferOutput<typeof OrganizationSchema>;
export type AdminDashboard = v.InferOutput<typeof AdminDashboardSchema>;
export type BillingPlanCode = v.InferOutput<typeof BillingPlanCodeSchema>;
export type BillingChannel = v.InferOutput<typeof BillingChannelSchema>;
export type CatalogPlan = v.InferOutput<typeof CatalogPlanSchema>;
export type BillingSubject = v.InferOutput<typeof BillingSubjectSchema>;
export type Entitlement = v.InferOutput<typeof EntitlementSchema>;
export type BillingSubscription = v.InferOutput<typeof BillingSubscriptionSchema>;
export type BillingCheckoutRequest = v.InferOutput<typeof BillingCheckoutRequestSchema>;
export type BillingPortalAction = v.InferOutput<typeof BillingPortalActionSchema>;
export type NativePurchaseValidation = v.InferOutput<typeof NativePurchaseValidationSchema>;
export type AdminEntitlementGrant = v.InferOutput<typeof AdminEntitlementGrantSchema>;
export type BillingUsageQuota = v.InferOutput<typeof BillingUsageQuotaSchema>;
export type EffectiveEntitlement = v.InferOutput<typeof EffectiveEntitlementSchema>;
